import { ReactNode, useEffect, useState } from 'react';
import { useTheme, styled } from '@mui/material/styles';
import { Box, Divider, Drawer, Grid, List, ListItem, Stack, Typography, Button, Chip } from '@mui/material';
import MainCard from 'components/MainCard';
import IconButton from 'components/@extended/IconButton';
import SimpleBar from 'components/third-party/SimpleBar';
import { Add, NotificationStatus, Slash } from 'iconsax-react';
import { ThemeMode } from 'types/config';
import serverCall from 'api/serverCall';
import api from 'api/api';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { OrderRequest, OrderRequestStatus } from 'types/custom/order';
import Amount from 'components/common/Amount';
import { dispatch } from 'store';
import { openSnackbar } from 'store/reducers/snackbar';
import { useWebsocketContext } from 'contexts/WebSocketContext';
import Avatar from 'components/@extended/Avatar';
import Alert from 'components/alert/CommonAlert';
import { formatDateCommon } from 'utils/formatters';
import timeAgo from 'utils/timeAgo';
import { useFirebaseContext } from 'contexts/FirebaseContext';
import { SimulationSource } from 'types/custom/simulation';
import dayjs from 'dayjs';
import StockDetailModal, { StockDetailState, emptyStockDetailState } from './StockDetailModal';

const StyledGridOverlay = styled(Stack)(({ theme }) => ({
  height: '400px',
  '& .ant-empty-img-1': {
    fill: theme.palette.mode === ThemeMode.DARK ? theme.palette.secondary[200] : theme.palette.secondary[400]
  },
  '& .ant-empty-img-2': {
    fill: theme.palette.secondary.light
  },
  '& .ant-empty-img-3': {
    fill: theme.palette.mode === ThemeMode.DARK ? theme.palette.secondary[400] : theme.palette.secondary[200]
  },
  '& .ant-empty-img-4': {
    fill: theme.palette.mode === ThemeMode.DARK ? theme.palette.secondary.dark : theme.palette.common.white
  },
  '& .ant-empty-img-5': {
    fillOpacity: theme.palette.mode === ThemeMode.DARK ? '0.09' : '0.95',
    fill: theme.palette.mode === ThemeMode.DARK ? theme.palette.secondary.darker : theme.palette.secondary.light
  }
}));

type Props = {
  open: boolean;
  setOpen: (val: boolean) => void;
  setRecommendationRequestCount: (val: number) => void;
};

const OrderRequestDrawerRecommendation: React.FC<Props> = ({ open, setOpen, setRecommendationRequestCount }) => {
  const theme = useTheme();
  const { orderRequestStatus, recommendation } = useWebsocketContext();
  const [orders, setOrders] = useState<OrderRequest[]>([]);
  const [isOpenCancelOrderConfirmation, setIsOpenCancelOrderConfirmation] = useState<boolean>(false);
  const { user } = useFirebaseContext();
  const permissions = user?.permissions;
  const hasApproveOrderPermission = permissions?.includes('canApproveOrderRequest');
  const [clearing, setClearing] = useState<boolean>(false);
  const [stockDetailState, setStockDetailState] = useState<StockDetailState>(emptyStockDetailState);

  const handleToggle = () => {
    setOpen(!open);
  };

  const fetchAllOrderRequests = () => {
    serverCall.get(api.getAllPendingOrdersForRecommendation(), (response, error) => {
      if (error) {
        console.error(error);
      } else {
        const sortedOrders = [...response.orders].sort(
          (a, b) => dayjs(b.createdOn, 'DD-MMM-YYYY hh:mm:ss a').valueOf() - dayjs(a.createdOn, 'DD-MMM-YYYY hh:mm:ss a').valueOf()
        );
        setOrders(sortedOrders);
      }
    });
  };

  useEffect(() => {
    fetchAllOrderRequests();
  }, [orderRequestStatus, recommendation]);

  const handleClearAll = () => {
    setClearing(true);
    serverCall.put(api.clearAllOrders(SimulationSource.RECOMMENDATION), {}, (response, error) => {
      if (error) {
        setClearing(false);
        dispatch(
          openSnackbar({
            open: true,
            close: true,
            message: `Error declining all orders`,
            variant: 'alert',
            alert: {
              color: 'error'
            },
            anchorOrigin: {
              vertical: 'top',
              horizontal: 'center'
            }
          })
        );
      } else {
        fetchAllOrderRequests();
        setRecommendationRequestCount(0);
        setClearing(false);
        setIsOpenCancelOrderConfirmation(false);
        dispatch(
          openSnackbar({
            open: true,
            close: true,
            message: `All orders cancelled successfully`,
            variant: 'alert',
            alert: {
              color: 'success'
            },
            anchorOrigin: {
              vertical: 'top',
              horizontal: 'center'
            }
          })
        );
      }
    });
  };

  const handleDeclineOrder = (requstId: string, orderId: string, userId: string, simulationId: string) => {
    serverCall.put(api.declineOrderRequest(requstId, orderId, simulationId), {}, (response, error) => {
      if (error) {
        dispatch(
          openSnackbar({
            open: true,
            close: true,
            message: `Error declining recommendation order ${orderId}`,
            variant: 'alert',
            alert: {
              color: 'error'
            },
            anchorOrigin: {
              vertical: 'top',
              horizontal: 'center'
            }
          })
        );
      } else {
        fetchAllOrderRequests();
        dispatch(
          openSnackbar({
            open: true,
            close: true,
            message: `Recommendation order ${orderId} declined successfully`,
            variant: 'alert',
            alert: {
              color: 'success'
            },
            anchorOrigin: {
              vertical: 'top',
              horizontal: 'center'
            }
          })
        );
      }
    });
  };

  // const handleMessage = (message: any) => {
  //   try {
  //     if (message) {
  //       const { requestId, status, message: text } = message;
  //       console.log(requestId, status, message, ' status >>>');

  //       if (status == 'approved') {
  //         fetchAllOrderRequests();

  //         dispatch(
  //           openSnackbar({
  //             open: true,
  //             close: true,
  //             message: text,
  //             variant: 'alert',
  //             alert: { color: 'success' },
  //             anchorOrigin: { vertical: 'top', horizontal: 'center' }
  //           })
  //         );
  //         // cleanup
  //         setActiveRequestIds((prev) => {
  //           const next = new Set(prev);
  //           next.delete(String(requestId));
  //           return next;
  //         });
  //         if (!activeRequestIds.has(String(requestId))) return;
  //       }
  //       if (status === 'cancelled') {
  //         dispatch(
  //           openSnackbar({
  //             open: true,
  //             close: true,
  //             message: text,
  //             variant: 'alert',
  //             alert: { color: 'error' },
  //             anchorOrigin: { vertical: 'top', horizontal: 'center' }
  //           })
  //         );
  //       }
  //     }
  //   } catch (error) {
  //     console.error('Error parsing message body:', error);
  //   }
  // };
  // useEffect(() => {
  //   const connectionStatus = WebSocketService.getConnectionStatus();

  //   WebSocketService.subscribe(`/topic/order-request/update`, handleMessage);
  //   console.log('/order-request/update connected >>>');
  //   return () => {
  //     if (connectionStatus === 'connected') {
  //       WebSocketService.unsubscribe('/order-request/update/');
  //     }
  //   };
  // }, []);

  const handleApproveOrder = (orderId: string) => {
    serverCall.put(api.approveOrderRequest(orderId, SimulationSource.RECOMMENDATION), {}, (response, error) => {
      if (error) {
        dispatch(
          openSnackbar({
            open: true,
            close: true,
            message: `Error approving recommendation order ${orderId}`,
            variant: 'alert',
            alert: {
              color: 'error'
            },
            anchorOrigin: {
              vertical: 'top',
              horizontal: 'center'
            }
          })
        );
      } else {
        fetchAllOrderRequests();
        dispatch(
          openSnackbar({
            open: true,
            close: true,
            message: `Recommendation order ${orderId} approved successfully`,
            variant: 'alert',
            alert: {
              color: 'success'
            },
            anchorOrigin: {
              vertical: 'top',
              horizontal: 'center'
            }
          })
        );
      }
    });
  };

  const handleCloseModal = () => {
    setIsOpenCancelOrderConfirmation(false);
  };

  const alertContent: ReactNode = (
    <Stack alignItems="center" spacing={3.5}>
      <Avatar color="warning" sx={{ width: 72, height: 72, fontSize: '1.75rem' }}>
        <Slash variant="Bold" />
      </Avatar>
      <Stack spacing={2}>
        <Typography variant="h4" align="center">
          Confirm Order Clearing
        </Typography>
        <Typography align="center">Are you sure you want to clear all orders?</Typography>
      </Stack>

      <Stack direction="row" spacing={2} sx={{ width: 1 }}>
        <Button fullWidth onClick={handleCloseModal} color="secondary" variant="outlined">
          No, Keep Orders
        </Button>
        <Button disabled={clearing} fullWidth color="primary" variant="contained" onClick={handleClearAll} autoFocus>
          {clearing ? 'Clearing...' : 'Yes, Clear Orders'}
        </Button>
      </Stack>
    </Stack>
  );

  const iconBackColorOpen = theme.palette.mode === ThemeMode.DARK ? 'secondary.200' : 'secondary.200';
  const iconBackColor = theme.palette.mode === ThemeMode.DARK ? 'background.default' : 'secondary.100';

  return (
    <>
      {isOpenCancelOrderConfirmation && (
        <Alert open={isOpenCancelOrderConfirmation} handleClose={handleCloseModal} content={alertContent} />
      )}

      {stockDetailState.open && <StockDetailModal state={stockDetailState} setState={setStockDetailState} />}
      <Box sx={{ flexShrink: 0, ml: 0.75 }}>
        <IconButton
          color="secondary"
          variant="light"
          onClick={handleToggle}
          aria-label="settings toggler"
          size="large"
          sx={{ color: 'secondary.main', bgcolor: open ? iconBackColorOpen : iconBackColor, p: 1 }}
        >
          <NotificationStatus variant="Bulk" />
        </IconButton>
      </Box>
      <Drawer sx={{ zIndex: 2001 }} anchor="right" onClose={handleToggle} open={open} PaperProps={{ sx: { width: { xs: 350, sm: 474 } } }}>
        {open && (
          <MainCard content={false} sx={{ border: 'none', borderRadius: 0, height: '100vh' }}>
            <SimpleBar
              sx={{
                '& .simplebar-content': {
                  display: 'flex',
                  flexDirection: 'column'
                }
              }}
            >
              <Box sx={{ p: 2.5 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
                  <Typography variant="h5">Recommendation order requests</Typography>
                  <IconButton color="secondary" sx={{ p: 0 }} onClick={handleToggle}>
                    <Add size={28} style={{ transform: 'rotate(45deg)' }} />
                  </IconButton>
                </Stack>
                <Divider sx={{ mt: 1.5, mb: 2 }} />
                {orders.length > 0 && (
                  <Box sx={{ mb: 2 }} justifyContent="end" display="flex">
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => {
                        setIsOpenCancelOrderConfirmation(true);
                      }}
                      startIcon={<CancelIcon />}
                    >
                      Clear All Orders
                    </Button>
                  </Box>
                )}
                <Grid>
                  {orders.length === 0 ? (
                    <StyledGridOverlay alignItems="center" justifyContent="center" spacing={1}>
                      <svg width="120" height="100" viewBox="0 0 184 152" aria-hidden focusable="false">
                        <g fill="none" fillRule="evenodd">
                          <g transform="translate(24 31.67)">
                            <ellipse className="ant-empty-img-5" cx="67.797" cy="106.89" rx="67.797" ry="12.668" />
                            <path
                              className="ant-empty-img-1"
                              d="M122.034 69.674L98.109 40.229c-1.148-1.386-2.826-2.225-4.593-2.225h-51.44c-1.766 0-3.444.839-4.592 2.225L13.56 69.674v15.383h108.475V69.674z"
                            />
                            <path
                              className="ant-empty-img-2"
                              d="M33.83 0h67.933a4 4 0 0 1 4 4v93.344a4 4 0 0 1-4 4H33.83a4 4 0 0 1-4-4V4a4 4 0 0 1 4-4z"
                            />
                            <path
                              className="ant-empty-img-3"
                              d="M42.678 9.953h50.237a2 2 0 0 1 2 2V36.91a2 2 0 0 1-2 2H42.678a2 2 0 0 1-2-2V11.953a2 2 0 0 1 2-2zM42.94 49.767h49.713a2.262 2.262 0 1 1 0 4.524H42.94a2.262 2.262 0 0 1 0-4.524zM42.94 61.53h49.713a2.262 2.262 0 1 1 0 4.525H42.94a2.262 2.262 0 0 1 0-4.525zM121.813 105.032c-.775 3.071-3.497 5.36-6.735 5.36H20.515c-3.238 0-5.96-2.29-6.734-5.36a7.309 7.309 0 0 1-.222-1.79V69.675h26.318c2.907 0 5.25 2.448 5.25 5.42v.04c0 2.971 2.37 5.37 5.277 5.37h34.785c2.907 0 5.277-2.421 5.277-5.393V75.1c0-2.972 2.343-5.426 5.25-5.426h26.318v33.569c0 .617-.077 1.216-.221 1.789z"
                            />
                          </g>
                          <path
                            className="ant-empty-img-3"
                            d="M149.121 33.292l-6.83 2.65a1 1 0 0 1-1.317-1.23l1.937-6.207c-2.589-2.944-4.109-6.534-4.109-10.408C138.802 8.102 148.92 0 161.402 0 173.881 0 184 8.102 184 18.097c0 9.995-10.118 18.097-22.599 18.097-4.528 0-8.744-1.066-12.28-2.902z"
                          />
                          <g className="ant-empty-img-4" transform="translate(149.65 15.383)">
                            <ellipse cx="20.654" cy="3.167" rx="2.849" ry="2.815" />
                            <path d="M5.698 5.63H0L2.898.704zM9.259.704h4.985V5.63H9.259z" />
                          </g>
                        </g>
                      </svg>
                      <Typography align="center">Oops! No orders requests found for recommendation</Typography>
                    </StyledGridOverlay>
                  ) : (
                    <List disablePadding sx={{ '& .MuiListItem-root': { py: 1.5, borderBottom: '1px solid', borderColor: 'divider' } }}>
                      {[...orders]
                        .sort((a, b) => new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime())
                        .map((order, i) => (
                          <ListItem
                            key={order.requestId}
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'stretch',
                              '&:hover': {
                                backgroundColor: 'action.hover'
                              }
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%', mb: 1 }}>
                              <Typography variant="subtitle2">
                                {order.firstName + ' ' + order.lastName + ' - ' + (order?.scenarioDisplayName ?? '')}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {order.createdOn ? timeAgo(order.createdOn) + ', ' + formatDateCommon(order.createdOn, 'HH:mm:ss') : 'N/A'}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%', mb: 1 }}>
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  fontWeight: 'bold',
                                  ...(order.stockId && {
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                    textDecorationStyle: 'dotted',
                                    '&:hover': { color: 'primary.main' }
                                  })
                                }}
                                onClick={() => order.stockId && setStockDetailState({ open: true, stockId: order.stockId })}
                                title={order.stockId ? 'View stock details' : undefined}
                              >
                                {order.stockId ?? '--'}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                              <Stack spacing={0.5} sx={{ flex: 1 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                                  QTY: {order.quantity} | Price:&nbsp;
                                  <Amount amount={order.price} variant="subtitle2" symbol="₹" />
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                                  Broker:&nbsp;
                                  {order.brokers}
                                  &nbsp;|&nbsp;Strategy: {order?.scenarioDisplayName}
                                </Typography>
                                {/* <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                                  Stoploss:&nbsp;
                                  <Amount amount={order.stoploss} variant="subtitle2" symbol="₹" />
                                  &nbsp;| Trigger Price:&nbsp;
                                  <Amount amount={order.triggerPrice} variant="subtitle2" symbol="₹" />
                                </Typography> */}
                              </Stack>
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                              >
                                <Typography
                                  variant="caption"
                                  color={order.transactionType === 'BUY' ? 'primary' : 'error'}
                                  sx={{ ml: 4, mb: 1, mt: -4, fontWeight: 'bold' }}
                                >
                                  {order.transactionType}
                                </Typography>
                                <Grid sx={{ mt: 5 }}>
                                  {order.orderRequestStatus === OrderRequestStatus.OPEN ? (
                                    hasApproveOrderPermission && (
                                      <>
                                        <IconButton
                                          color="error"
                                          aria-label="cancel"
                                          size="medium"
                                          sx={{ p: 0.5 }}
                                          onClick={() =>
                                            handleDeclineOrder(
                                              order.requestId.toString(),
                                              order.orderId.toString(),
                                              order.userId,
                                              order.simulationId
                                            )
                                          }
                                        >
                                          <CancelIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                          color="success"
                                          aria-label="approve"
                                          size="medium"
                                          sx={{ p: 0.5 }}
                                          onClick={() => handleApproveOrder(order.requestId.toString())}
                                        >
                                          <CheckCircleIcon fontSize="small" />
                                        </IconButton>
                                      </>
                                    )
                                  ) : (
                                    <Chip
                                      label={order.orderRequestStatus}
                                      color={
                                        order.orderRequestStatus === OrderRequestStatus.CANCELLED ||
                                        order.orderRequestStatus === OrderRequestStatus.REJECTED
                                          ? 'error'
                                          : 'success'
                                      }
                                      size="small"
                                      variant="light"
                                    />
                                  )}
                                </Grid>
                              </Stack>
                            </Box>
                          </ListItem>
                        ))}
                    </List>
                  )}
                </Grid>
              </Box>
            </SimpleBar>
          </MainCard>
        )}
      </Drawer>
    </>
  );
};

export default OrderRequestDrawerRecommendation;
